import React, {KeyboardEvent, useEffect, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "headlessui-v2";
import {twJoin} from "tailwind-merge";
import {useQuery} from "@tanstack/react-query";
import {Button, Icon} from "peggirkit";
import {ChevronRightIcon, MagnifyingGlassIcon} from "@heroicons/react/20/solid";
import {UsersIcon} from "@heroicons/react/24/outline";
import {CustomerSearchResult} from "@/data/entities/user";
import {formatAddress} from "@/data/entities/deliveryAddress";
import {SetStateFn} from "@/utils/SetStateUtils";
import {api} from "@/data/api";

/** The backend requires at least 2 characters. */
const MIN_QUERY_LENGTH = 2;

type Props = {
    show: boolean,
    setShow: SetStateFn<boolean>,
    onSelect: (customer: CustomerSearchResult) => void,
};

const fullName = (customer: CustomerSearchResult): string =>
    `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim();

/**
 * Command palette to find a customer account. Only searches when pressing
 * Enter or the search button, not while typing. After searching, the arrow
 * keys move through the results and Enter selects one.
 *
 * Uses a Headless UI v2 dialog, like the product modal of the POS.
 */
const CustomerSearchModal = ({show, setShow, onSelect}: Props) => {
    const intl = useIntl();
    const [query, setQuery] = useState("");
    const [searchedTerm, setSearchedTerm] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const {data: results, isFetching, isError, refetch} = useQuery({
        queryKey: ["pos-customer-search", searchedTerm],
        queryFn: () => api.user.searchCustomers(searchedTerm!),
        enabled: searchedTerm !== null,
        staleTime: 30_000,
        retry: false,
    });

    const term: string = query.trim();
    const canSearch: boolean = term.length >= MIN_QUERY_LENGTH && !isFetching;
    // Results of an earlier search disappear as soon as the query changes
    const isCurrent: boolean = searchedTerm !== null && term === searchedTerm;
    const customers: CustomerSearchResult[] = isCurrent ? (results ?? []) : [];
    const activeCustomer: CustomerSearchResult | null =
        activeIndex === null ? null : customers[activeIndex] ?? null;

    // Start empty every time the palette opens
    useEffect(() => {
        if (!show) return;
        setQuery("");
        setSearchedTerm(null);
        setActiveIndex(null);
    }, [show]);

    // New results: the first one is active, so Enter selects it
    useEffect(() => {
        setActiveIndex(isCurrent && (results?.length ?? 0) > 0 ? 0 : null);
    }, [results, isCurrent]);

    // Keep the active result in view
    useEffect(() => {
        if (activeIndex !== null)
            optionRefs.current[activeIndex]?.scrollIntoView({block: "nearest"});
    }, [activeIndex]);

    const search = () => {
        if (!canSearch)
            return;

        setActiveIndex(null);
        if (term === searchedTerm)
            void refetch();
        else
            setSearchedTerm(term);
    };

    const close = () => setShow(false);
    const select = (customer: CustomerSearchResult) => {
        onSelect(customer);
        close();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case "ArrowDown":
                if (customers.length === 0) return;
                event.preventDefault();
                setActiveIndex(index => index === null ? 0 : Math.min(index + 1, customers.length - 1));
                break;
            case "ArrowUp":
                if (customers.length === 0) return;
                event.preventDefault();
                setActiveIndex(index => index === null ? 0 : Math.max(index - 1, 0));
                break;
            case "Enter":
                event.preventDefault();
                // Results of this query: select. Otherwise: search.
                if (activeCustomer !== null)
                    select(activeCustomer);
                else
                    search();
                break;
        }
    };

    return (
        <Dialog
            open={show}
            onClose={close}
            className={"relative z-50"}
        >
            <DialogBackdrop
                transition
                className={"fixed inset-0 bg-gray-500/75 dark:bg-slate-600/75 transition-opacity duration-300 ease-out data-[closed]:opacity-0 data-[leave]:duration-200 data-[leave]:ease-in"}
            />

            <div className={"fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20"}>
                <DialogPanel
                    transition
                    className={twJoin(
                        "mx-auto max-w-4xl overflow-hidden rounded-xl bg-white dark:bg-slate-800 shadow-2xl",
                        "divide-y divide-gray-100 dark:divide-slate-700",
                        "transition-all duration-300 ease-out data-[closed]:opacity-0 data-[closed]:scale-95 data-[leave]:duration-200 data-[leave]:ease-in",
                    )}
                >
                    <DialogTitle className={"sr-only"}>
                        <FormattedMessage id={"searchCustomerButton"}/>
                    </DialogTitle>

                    {/* Search field */}
                    <div className={"flex flex-row items-center gap-3 px-4"}>
                        <MagnifyingGlassIcon
                            className={"size-5 shrink-0 text-gray-400 dark:text-slate-500"}
                            aria-hidden={"true"}
                        />
                        <input
                            autoFocus
                            type={"text"}
                            className={twJoin(
                                "h-14 w-full min-w-0 border-0 bg-transparent px-0 text-base outline-none focus:ring-0",
                                "text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500",
                            )}
                            placeholder={intl.formatMessage({id: "searchCustomerPlaceholder"})}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            aria-activedescendant={activeIndex === null ? undefined : `customer-option-${activeIndex}`}
                        />
                        <Button
                            size={"sm"}
                            disabled={!canSearch}
                            loading={isFetching}
                            iconBefore={<Icon icon={MagnifyingGlassIcon}/>}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: e => {
                                        e.preventDefault();
                                        search();
                                    },
                                },
                            }}
                        >
                            <FormattedMessage id={isFetching ? "searching" : "search"}/>
                        </Button>
                    </div>

                    {/* Before searching / after changing the query */}
                    {
                        !isCurrent &&
                        <p className={"px-6 py-10 text-center text-sm text-gray-500 dark:text-slate-400"}>
                            <FormattedMessage id={"searchCustomerHint"}/>
                        </p>
                    }

                    {/* Search failed */}
                    {
                        isCurrent && isError && !isFetching &&
                        <p role={"alert"} className={"px-6 py-4 text-sm text-red-600 dark:text-red-400"}>
                            <FormattedMessage id={"searchCustomerFailed"}/>
                        </p>
                    }

                    {/* Nothing found */}
                    {
                        isCurrent && !isError && !isFetching && customers.length === 0 &&
                        <div className={"px-6 py-14 text-center text-sm sm:px-14"}>
                            <UsersIcon className={"mx-auto size-6 text-gray-400 dark:text-slate-500"}
                                       aria-hidden={"true"}/>
                            <p className={"mt-4 font-semibold text-gray-900 dark:text-white"}>
                                <FormattedMessage id={"noCustomersFound"}/>
                            </p>
                            <p className={"mt-2 text-gray-500 dark:text-slate-400"}>
                                <FormattedMessage id={"noCustomersFoundDescription"}/>
                            </p>
                        </div>
                    }

                    {/* Results, with details of the active one */}
                    {
                        customers.length > 0 &&
                        <div className={"flex flex-row divide-x divide-gray-100 dark:divide-slate-700"}>
                            <div
                                role={"listbox"}
                                className={"max-h-96 min-w-0 flex-auto overflow-y-auto scroll-py-2 p-2"}
                            >
                                {customers.map((customer: CustomerSearchResult, index: number) => (
                                    <button
                                        key={customer.id}
                                        id={`customer-option-${index}`}
                                        ref={element => {
                                            optionRefs.current[index] = element;
                                        }}
                                        type={"button"}
                                        role={"option"}
                                        aria-selected={index === activeIndex}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => select(customer)}
                                        className={twJoin(
                                            "group w-full flex flex-row items-center gap-3 rounded-md p-2 text-left text-sm",
                                            index === activeIndex
                                                ? "bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
                                                : "text-gray-700 dark:text-slate-300",
                                        )}
                                    >
                                        <span className={"min-w-0 flex-auto"}>
                                            <span className={"block truncate font-medium"}>{fullName(customer)}</span>
                                            <span className={"block truncate text-gray-500 dark:text-slate-400"}>
                                                {customer.phoneNumber} • {customer.emailAddress}
                                            </span>
                                        </span>
                                        <ChevronRightIcon
                                            className={twJoin(
                                                "size-5 shrink-0 text-gray-400",
                                                index === activeIndex ? "block" : "hidden",
                                            )}
                                            aria-hidden={"true"}
                                        />
                                    </button>
                                ))}
                            </div>

                            {
                                activeCustomer !== null &&
                                <CustomerPreview
                                    customer={activeCustomer}
                                    onSelect={() => select(activeCustomer)}
                                />
                            }
                        </div>
                    }
                </DialogPanel>
            </div>
        </Dialog>
    );
};

type PreviewProps = {
    customer: CustomerSearchResult,
    onSelect: () => void,
};

/**
 * Details of the active result, to check it's the right customer.
 */
const CustomerPreview = ({customer, onSelect}: PreviewProps) => {
    const intl = useIntl();

    return (
        <div className={"hidden sm:flex w-1/2 max-h-96 flex-none flex-col overflow-y-auto"}>
            <div className={"p-6 text-center border-b border-gray-100 dark:border-slate-700"}>
                <h2 className={"font-semibold text-gray-900 dark:text-white"}>{fullName(customer)}</h2>
                <p className={"text-sm text-gray-500 dark:text-slate-400"}>
                    {intl.formatMessage({id: "n-points"}, {n: customer.loyaltyPoints})}
                </p>
            </div>

            <div className={"flex flex-auto flex-col justify-between gap-6 p-6"}>
                <dl className={"grid grid-cols-1 gap-y-3 text-sm"}>
                    <PreviewItem title={intl.formatMessage({id: "emailAddress"})} value={customer.emailAddress}/>
                    <PreviewItem title={intl.formatMessage({id: "phoneNumber"})} value={customer.phoneNumber}/>
                    {
                        customer.deliveryAddresses.length > 0 &&
                        <PreviewItem
                            title={intl.formatMessage({id: "deliveryAddresses"})}
                            value={
                                <ul>
                                    {customer.deliveryAddresses.map(address => (
                                        <li key={address.id}>{address.name}: {formatAddress(address)}</li>
                                    ))}
                                </ul>
                            }
                        />
                    }
                    {
                        customer.businessDetails !== null &&
                        <PreviewItem
                            title={intl.formatMessage({id: "businessDetails"})}
                            value={customer.businessDetails.organisationName}
                        />
                    }
                </dl>

                <Button
                    width={"w-full"}
                    align={"center"}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                onSelect();
                            },
                        },
                    }}
                >
                    <FormattedMessage id={"selectThisCustomer"}/>
                </Button>
            </div>
        </div>
    );
};

const PreviewItem = ({title, value}: { title: string, value: React.ReactNode }) => (
    <div>
        <dt className={"font-medium text-gray-900 dark:text-white"}>{title}</dt>
        <dd className={"text-gray-600 dark:text-slate-300 break-words"}>{value}</dd>
    </div>
);

export default CustomerSearchModal;